import axios from 'axios';
import type { TIsErrorFn } from '@alfa-bank/site-form-helpers/src/meta/models';
import type { FieldMetaState, FieldInputProps } from 'react-final-form';
import { isError } from '@alfa-bank/site-form-helpers/meta/is-error';
import { DADATA_PROXY_SUGGEST_ADDRESS } from '@alfa-bank/site-envconfig';
import type { IFormattedAddressSuggestionData } from '@alfa-bank/site-dc-form-api/src/models';

import { DATA_SUFFIX, ADDRESS_CAPTION } from '../../config';

import type {
    IAddressResponse,
    ISuggestions,
    IResponseSuggestionData,
    IAddressFieldPublicProps,
} from './models';

const convertDadataAddress = (
    dadataAddress: IResponseSuggestionData,
    value: string
): IFormattedAddressSuggestionData => {
    return {
        cityName: dadataAddress.city,
        cityKladrId: dadataAddress.city_kladr_id,
        fullName: value,
        house: dadataAddress.house,
        regionKladrId: dadataAddress.region_kladr_id,
        regionName: dadataAddress.region,
        settlementName: dadataAddress.settlement,
        streetName: dadataAddress.street,
        latitude: dadataAddress.geo_lat,
        longitude: dadataAddress.geo_lon,
        qcGeo: dadataAddress.qc_geo,
    };
};

/** Функция возвращает массив подсказок для адреса */
export const getSuggestionAddress = async (address: string): Promise<ISuggestions[]> => {
    const {
        data: { suggestions },
    } = await axios.post<IAddressResponse>(DADATA_PROXY_SUGGEST_ADDRESS(), {
        query: address,
    });

    return suggestions.map(item => {
        const { data, value } = item;
        const isEndAddress = Boolean(
            data.region && data.postal_code && (data.city || data.settlement)
        );

        return {
            value,
            data: convertDadataAddress(data, value),
            label: item.value,
            id: item.value,
            isEndAddress,
        };
    });
};

/** Функция проверяет конечный это адрес или нет */
export const validateEndAddress = (
    value: string,
    data?: Partial<IFormattedAddressSuggestionData>
) =>
    !!(
        data?.fullName === value &&
        data?.regionName &&
        data?.house &&
        (data?.cityName || data?.settlementName)
    );

export const dataFieldName = (name: string) => `${name}${DATA_SUFFIX}`;

export const createAddressFieldValidator =
    (fieldName: string, messages: IAddressFieldPublicProps['errorText']) =>
    // TODO: поправить типизацию
    (value: string, allValues: unknown) => {
        const data = (allValues as Record<string, string | IFormattedAddressSuggestionData>)[
            dataFieldName(fieldName)
        ];
        const isSuggestions = (
            allValues as Record<string, string | IFormattedAddressSuggestionData>
        )[`${fieldName}-isSuggestions`];

        // пользователь указал несуществующий адрес (dadata вернула пустой массив suggestions)
        if (value && !isSuggestions) {
            return messages.nonexistentAddress;
        }
        // пользователь не выбрал подсказку из списка (dadata вернула массив suggestions с подсказками (объектами))
        if (isSuggestions && value && !data) {
            return messages.addressNotSelected;
        }
        if (!data || typeof data === 'string') {
            return messages.empty;
        }
        // если пользователь не указал населенный пункт (в выбранном варианте ответа от dadata нет полей suggestions.data.city и suggestions.data.settlement)
        if (!data.cityName && !data.settlementName) {
            return messages.noLocality;
        }
        // если пользователь не указал улицу или номер дома (в выбранном варианте ответа от dadata нет полей suggestions.data.settlement и suggestions.data.street и suggestions.data.house)
        if (!data.settlementName && !data.streetName && !data.house) {
            return messages.noStreetOrHouse;
        }
        // если пользователь не указал номер дома (в выбранном варианте ответа от dadata нет поля suggestions.data.house)
        if (!data.house) {
            return messages.noHouse;
        }

        return validateEndAddress(value, data) ? undefined : messages.empty;
    };

export const createAddressCaption =
    (isErrorFn: TIsErrorFn) =>
    (meta: FieldMetaState<string>, caption = '', value?: string) => {
        if (isErrorFn(meta, value)) {
            return meta.error || meta.submitError;
        }

        return meta?.dirty ? ADDRESS_CAPTION : caption;
    };

export const getAddressCaption = createAddressCaption(isError);

// Хелпер возвращает строку с запятой и пробелом в конце
export const addCommaToAddress = (str: string) => {
    // Кейс с редактированием пользователем адреса после подстановки из селекта и нажатия на enter
    if (str.trimEnd().endsWith(',')) {
        return `${str.trimEnd()} `;
    }

    return `${str}, `;
};

export const isAddressSuccess = (
    {
        dirtySinceLastSubmit,
        error,
        submitError,
        submitSucceeded,
        submitting,
        touched,
    }: FieldMetaState<string>,
    value: FieldInputProps<string>['value']
) => {
    /**
     * При отправке формы React Final Form аннулирует
     * свойство touched, что может приводить к
     * незапланированной смене success-статуса, поэтому
     * если поле было отправлено, то мы это расцениваем
     * равнозначно touched, чтоб не вводить пользователя
     * в заблуждение
     */
    const submitted = submitSucceeded || submitting;

    /** Отличие от стандартного isSuccess: кейс для случая, когда инпут рендерится уже со значением, то есть touched = false
     */
    if (value && !touched && !error && !submitError) return true;

    if (!touched && !submitted) return undefined;
    if (error) return false;
    if (submitError) return dirtySinceLastSubmit;

    return true;
};
